/* eslint-disable prettier/prettier */
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { Button, useAlert } from "@cd2/components/build/es6"
import { useSkeem } from "./providers/SkeemProvider"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { renderRoutes } from "react-router-config"
import { matchPath, Redirect, useHistory, useLocation } from "react-router-dom"
import { ErrorBoundary } from "./ErrorBoundary"
import { useAsyncEffect } from "./hooks/useAsyncEffect"
import { useInterval } from "./hooks/useInterval"
import { AutoLogoutWhenKicked } from "./interfaceParts/AutoLogoutWhenKicked"
import { ErrorSplashScreen } from "./interfaceParts/ErrorSplashScreen"
import { NullErrorBoundary } from "./interfaceParts/NullErrorBoundary"
import { RequirePasswordUpdate } from "./interfaceParts/RequirePasswordUpdate"
import { ResultPdfGenerationStatus } from "./interfaceParts/ResultPdfGenerationStatus"
import { RotaWrapper } from "./interfaceParts/RotaWrapper"
import { SessionWrapper } from "./interfaceParts/SessionWrapper"
import { SplashScreen } from "./interfaceParts/SplashScreen"
import { TermsAndConditionsPopup } from "./interfaceParts/TermsAndConditionsPopup"
import { UserSiteWrapper } from "./interfaceParts/UserSiteWrapper"
import { BillingStatusProvider, useBillingStatus } from "./providers/BillingStatusProvider"
import { CompanyThemeLoader } from "./providers/CompanyThemeProvider"
import { eRoutes, routes } from "./routes"
import { AuthorizationHeartbeat } from "./interfaceParts/AuthorizationHeartbeat"
import { ActivityHeartbeat } from "./interfaceParts/ActivityHeartbeat"
import { switchCompany } from "./interfaceParts/CompanySwitcher"
import { AdminCompaniesGlobalStateProvider, AnonymousGlobalStateProvider, GlobalStateProvider, useGlobalState } from "./utils/globalState"
import { UserManageBillingInfo } from "./pages/user/UserManageBillingInfo"
import { useReloadKey } from "./hooks/useReloadKey"
import { assert } from "@cd2/tools"
import { IRecord } from "./types/schema"
import { ManagedModalProvider } from "./providers/ManagedModals"
import { TextGroupLarge } from "./interfaceParts/Title"
import { LogoutPage } from "./pages/session/LogoutPage"
import { IsLoggedInPage } from "./pages/session/IsLoggedInPage"
import Smartlook from "smartlook-client"
import { useAnalyticsPageViews } from "./utils/ga"

const AdminSiteWrapper = namedLazy(() => import("./interfaceParts/AdminSiteWrapper"), "AdminSiteWrapper")
const EmployeeAppraisalsSetupFlow = namedLazy(() => import("./pages/user/EmployeeAppraisalsSetupFlow"), "EmployeeAppraisalsSetupFlow")
const CaseCoachingTakingPage = namedLazy(() => import("./pages/user/CaseCoachingTakingPage"), "CaseCoachingTakingPage")
const PageNotFoundPage = namedLazy(()=> import("./pages/admin/PageNotFoundPage"), "PageNotFoundPage")
const QuestionsEditPage = namedLazy(()=> import("./pages/admin/QuestionsEditPage"), "QuestionsEditPage")
const QuestionsNewPage = namedLazy(()=> import("./pages/admin/QuestionsNewPage"), "QuestionsNewPage")
const ResolutionsEditPage = namedLazy(()=> import("./pages/admin/ResolutionsEditPage"), "ResolutionsEditPage")
const ResolutionsNewPage = namedLazy(()=> import("./pages/admin/ResolutionsNewPage"), "ResolutionsNewPage")
const ResultsShowPage = namedLazy(()=> import("./pages/admin/ResultsShowPage"), "ResultsShowPage")
const SkeemFlowProvider = namedLazy(() => import("./pages/user/CaseCoachingTakingPage/SkeemFlowContext"), "SkeemFlowProvider")
const UserCanSeeHrAreas = namedLazy(() => import("./pages/user/UserCanSeeHrAreas"), "UserCanSeeHrAreas")
const UserFlowsIntroPage = namedLazy(()=> import("./pages/user/UserFlowsIntroPage"), "UserFlowsIntroPage")
const UserResultsShowPage = namedLazy(()=> import("./pages/user/UserResultsShowPage"), "UserResultsShowPage")


function namedLazy<Mod extends object, Key extends keyof Mod>(
  importer: () => Promise<Mod>,
  key: Key,
) {
  return React.lazy(() => importer().then(mod => ({ default: mod[key] as any })))
}

type IRoute = {
  path: string
  exact?: boolean
  component:
  | ((props: { termsAccepted?: boolean; companyId?: string; isAdmin?: boolean }) => JSX.Element)
  | ((props: { termsAccepted?: boolean; companyId?: string; isAdmin?: boolean }) => React.ReactNode)
  routes?: IRoute[]

  noWrapperPadding?: boolean
  noFooter?: boolean
  hasSidebar?: boolean
}

function routesWithWrapper(path: eRoutes, Wrapper: React.ComponentType<{ children: React.ReactNode }>, routes: IRoute[]): IRoute {
  return {
    path: path,
    component: ({ route }: any) => <Wrapper children={renderRoutes(route.routes)} />,
    routes: [
      ...routes.map(route => ({
        exact: ("routes" in route ? false : true),
        ...route,
      })),
      { path: "*", component: () => <PageNotFoundPage /> },
    ],
    exact: false,
  }
}

const termsRoutes = [
  { path: "/terms-and-conditions", component: namedLazy(() => import("./pages/user/IndividualUserTermsPage"), "IndividualUserTermsPage") },
  { path: "/saas", component: namedLazy(() => import("./pages/user/SaaSTermsPage"), "SaaSTermsPage") },
  { path: "/eula", component: namedLazy(() => import("./pages/user/AcceptableUsePolicyPage"), "AcceptableUsePolicyPage") },
  { path: "/privacy-policy", component: namedLazy(() => import("./pages/user/PrivacyPolicyPage"), "PrivacyPolicyPage") },
]

const anonymousRoutes: (IRoute & { cta?:"sign-in" | "sign-up" })[] = [
  { path: eRoutes["/sign-in"], cta: "sign-up", exact: true, component: namedLazy(() => import("./pages/session/SignInPage"), "SignInPage") },
  { path: eRoutes["/sign-up"], cta: "sign-in", component: namedLazy(() => import("./pages/session/SignUpPage"), "SignUpPage") },
  { path: "/sign-up/hr", exact: true, component: namedLazy(() => import("./pages/session/SignUpHrProfessionalPage"), "SignUpHrProfessionalPage") },
  { path: eRoutes["/forgot-password"], component: namedLazy(() => import("./pages/session/ForgottenPasswordPage"), "ForgottenPasswordPage") },
  { path: eRoutes["/reset-password"], component: namedLazy(() => import("./pages/session/ResetPasswordPage"), "ResetPasswordPage") },
  { path: "/accept-invite", component: namedLazy(() => import("./pages/session/AcceptInvitePage"), "AcceptInvitePage") },
  { path: "/employee-invite", component: namedLazy(() => import("./pages/session/EmployeeInvitePage"), "EmployeeInvitePage") },
  { path: "/case-coaching/index", component: namedLazy(() => import("./pages/anon/AnonDemoFlowListPage"), "AnonDemoFlowListPage"), exact: true },
  { path: "/case-coaching/:flowId/begin", component: namedLazy(() => import("./pages/anon/AnonBeginFlowPage"), "AnonBeginFlowPage"), exact: true },
  {
    path: "/case-coaching/:resultId",
    component: () => (
      <SkeemFlowProvider>
        <CaseCoachingTakingPage />
      </SkeemFlowProvider>
    ),
    exact: true,
  },
  { path: "/case-coaching/:id/anon-results", component: namedLazy(() => import("./pages/anon/AnonFlowResultsPage"), "AnonFlowResultsPage"), exact: true },
  {
    path: "/case-coaching/:id/results",
    exact: true,
    component: () => (
      <Redirect
        to={{
          pathname: "/",
          state: { returnTo: `${window.location.pathname}${window.location.search}` },
        }}
      />
    ),
  },
  ...termsRoutes,
  {
    path: "",
    component: () => (
      <Redirect
        to={{
          pathname: "/",
          state: { returnTo: `${window.location.pathname}${window.location.search}` },
        }}
      />
    ),
  },
]

const anonymousPathRedirects = anonymousRoutes
  .filter(route => !["/", "", "/employee-invite"].includes(route.path))
  .map(route => ({
    path: route.path,
    component: () => <Redirect to="/" />,
  }))

const employeeInviteAuthenticatedRoute = {
  path: "/employee-invite",
  exact: true,
  component: namedLazy(
    () => import("./pages/session/EmployeeInviteAuthenticatedPage"),
    "EmployeeInviteAuthenticatedPage",
  ),
  hasSidebar: false,
}

export const userHrRoutes = [
  { path: eRoutes["/recruitment"], exact: true, component: namedLazy(()=> import("./pages/user/UserRecruitmentDashboard"), "UserRecruitmentDashboard") },
  { path: eRoutes["/hr-profiles"], exact: true, component: namedLazy(()=> import("./pages/user/UserRecruitmentIndexPage"), "UserRecruitmentIndexPage") },
  { path: eRoutes["/hr-profiles/:id/request"], component: namedLazy(()=> import("./pages/user/UserRecruitmentRequestPage"), "UserRecruitmentRequestPage") },
  { path: eRoutes["/hr-profiles/:id"], component: namedLazy(()=> import("./pages/user/UserRecruitmentShowPage"), "UserRecruitmentShowPage") },
  { path: "/job-requests/:id", component: namedLazy(()=> import("./pages/user/UserJobRequestsShowPage"), "UserJobRequestsShowPage") },
  { path: eRoutes["/hr-team"], component: namedLazy(()=> import("./pages/user/UserRecruitmentHrTeamIndexPage"), "UserRecruitmentHrTeamIndexPage") },
  { path: "/appraisals", component: namedLazy(() => import("./pages/user/Appraisals/AppraisalsIndexPage"), "AppraisalsIndexPage"), exact: true },
  { path: "/appraisals/:id", component: namedLazy(() => import("./pages/user/Appraisals/AppraisalsShowPage"), "AppraisalsShowPage"), exact: true },
]

export const userRoutes: IRoute[] = [
  { path: "/", exact: true, component: namedLazy(() => import("./pages/user/UserDashboardPage"), "UserDashboardPage"), noWrapperPadding: true, hasSidebar: true },

  routesWithWrapper(
    eRoutes["/teams/:id"],
    namedLazy(() => import("./pages/user/UserManageTeamShowPageWrapper"), "UserManageTeamShowPageWrapper"),
    [
      { path: eRoutes["/teams/:id"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamShowPage") },
      { path: eRoutes["/teams/:id/employees"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamEmployeesPage") },
      { path: eRoutes["/teams/:id/employees/import"], component: namedLazy(() => import("./pages/user/UserManageTeamShowImportPage"), "UserManageTeamShowImportPage") },
      { path: eRoutes["/teams/:id/users"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamUsersPage") },
      { path: eRoutes["/teams/:id/cases"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamCasesPage") },
      { path: eRoutes["/teams/:id/edit"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamEditPage") },
      { path: eRoutes["/teams/:id/delete"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamDeletePage") },
    ]
  ),

  { path: "/flows", hasSidebar: true, component: namedLazy(()=> import("./pages/user/UserFlowsIndexPage"), "UserFlowsIndexPage") },
  {
    path: "/flows-intro", component: () => (
      <SkeemFlowProvider>
        <UserFlowsIntroPage />
      </SkeemFlowProvider>
    ),
  },
  { path: "/case-coaching/:id/delete", hasSidebar: true, component: namedLazy(()=> import("./pages/user/UserResultsDeletePage"), "UserResultsDeletePage") },
  { path: "/case-coaching/:flowId/begin", component: namedLazy(() => import("./pages/user/CaseCoachingBeginPage"), "CaseCoachingBeginPage"), exact: true },
  {
    path: "/case-coaching/:resultId",
    component: () => (
      <SkeemFlowProvider>
        <CaseCoachingTakingPage />
      </SkeemFlowProvider>
    ),
    exact: true,
  },
  {
    path: "/case-coaching/:id/results", hasSidebar: true,
    component: () => (
      <SkeemFlowProvider>
        <UserResultsShowPage />
      </SkeemFlowProvider>
    ),
    exact: true,
  },
  { path: "/results", component: namedLazy(() => import("./pages/user/FlowResults/ResultsIndex"), "ResultsIndex"), hasSidebar: true, },
  { path: "/reminders", component: namedLazy(()=> import("./pages/user/UserRemindersIndexPage"), "UserRemindersIndexPage") },
  { path: "/appraisals", component: namedLazy(() => import("./pages/user/Appraisals/AppraisalsIndexPage"), "AppraisalsIndexPage"), exact: true },
  { path: "/appraisals/:id", component: namedLazy(() => import("./pages/user/Appraisals/AppraisalsShowPage"), "AppraisalsShowPage"), exact: true },
  { path: "/manage-appraisals", component: namedLazy(() => import("./forms/CompanyEmployeesIndexPage"), "CompanyEmployeesIndexPage") },
  { path: routes.companyManageDepartments, exact: true, component: namedLazy(()=> import("./pages/company/CompanyManageDepartmentsPage"), "CompanyManageDepartmentsPage") },

  { path: "/manage-team", exact: true, component: namedLazy(() => import("./pages/user/CompanySettingsPage"), "CompanySettingsPage") },
  { path: "/manage-team/details", exact: true, component: namedLazy(() => import("./pages/user/CompanyDetailsPage"), "CompanyDetailsPage") },
  { path: "/manage-team/teams", exact: true, component: namedLazy(()=> import("./pages/company/CompanyPeoplesTeamsIndex"), "CompanyPeoplesTeamsIndex") },
  { path: "/manage-team/fourth", exact: true, component: namedLazy(() => import("./pages/company/CompanyManageFourthPage"), "CompanyManageFourthPage") },
  { path: eRoutes["/manage-team/itrent"], exact: true, component: namedLazy(() => import("./pages/company/CompanyManageITrentPage"), "CompanyManageITrentPage") },
  { path: eRoutes["/teams"], exact: true, component: namedLazy(() => import( "./pages/user/MyTeams/UserTeamsIndexPage"), "UserTeamsIndexPage") },
  routesWithWrapper(
    eRoutes["/teams/:id"],
    namedLazy(() => import("./pages/user/UserManageTeamShowPageWrapper"), "UserManageTeamShowPageWrapper"),
    [
      { path: eRoutes["/teams/:id"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamShowPage") },
      { path: eRoutes["/teams/:id/employees"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamEmployeesPage") },
      { path: eRoutes["/teams/:id/employees/import"], component: namedLazy(() => import("./pages/user/UserManageTeamShowImportPage"), "UserManageTeamShowImportPage") },
      { path: eRoutes["/teams/:id/users"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamUsersPage") },
      { path: eRoutes["/teams/:id/cases"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamCasesPage") },
      { path: eRoutes["/teams/:id/edit"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamEditPage") },
      { path: eRoutes["/teams/:id/delete"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamDeletePage") },
    ]
  ),
  { path: "/manage-team/teams/import", component: namedLazy(()=> import("./forms/CompanyTeamsImportPage"), "CompanyTeamsImportPage") },
  routesWithWrapper(
    eRoutes["/manage-team/teams/:id"],
    namedLazy(() => import("./pages/user/UserManageTeamShowPageWrapper"), "UserManageTeamShowPageWrapper"),
    [
      { path: eRoutes["/manage-team/teams/:id"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamShowPage") },
      { path: eRoutes["/manage-team/teams/:id/employees"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamEmployeesPage") },
      { path: eRoutes["/manage-team/teams/:id/employees/import"], component: namedLazy(() => import("./pages/user/UserManageTeamShowImportPage"), "UserManageTeamShowImportPage") },
      { path: eRoutes["/manage-team/teams/:id/users"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamUsersPage") },
      { path: eRoutes["/manage-team/teams/:id/cases"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamCasesPage") },
      { path: eRoutes["/manage-team/teams/:id/edit"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamEditPage") },
      { path: eRoutes["/manage-team/teams/:id/delete"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamDeletePage") },
    ]
  ),
  { path: "/manage-team/tasks", exact: true, component: namedLazy(()=> import("./pages/company/CompanyRemindersPage"), "CompanyRemindersPage") },
  {
    path: eRoutes["/probations"],
    exact: true,
    hasSidebar: true,
    component: namedLazy(() => import("./pages/company/ProbationIndexPage"), "ProbationIndexPage"),
  },
  { path: "/manage-team/employee-tags", exact: true, component: namedLazy(() => import("./pages/EmployeeTags/EmployeeTagsIndexPage"), "EmployeeTagsIndexPage") },
  { path: "/manage-team/settings", exact: true, component: namedLazy(() => import("./pages/user/CompanyPlatformFunctionalityPage"), "CompanySettingsPlatformFunctionalityPage") },
  { path: "/manage-team/sso", exact: true, component: namedLazy(() => import("./pages/company/CompanySsoSettingsPage"), "CompanySsoSettingsPage") },
  { path: "/manage-team/alerts", exact: true, component: namedLazy(() => import("./forms/CompanyActionTagsSection"), "CompanyActionTagsSection") },
  { path: "/manage-team/updates", exact: true, component: namedLazy(()=> import("./pages/company/CompanyManageUpdatesPage"), "CompanyManageUpdatesPage") },
  { path: eRoutes["/manage-team/help-requests"], exact: true, component: namedLazy(() => import("./pages/company/CompanyManageHelpRequestsIndexPage"), "CompanyManageHelpRequestsIndexPage") },
  { path: eRoutes["/manage-team/help-requests/:id"], exact: true, component: namedLazy(() => import("./pages/company/CompanyManageHelpRequestsShowPage"), "CompanyManageHelpRequestsShowPage") },
  { path: "/manage-team/updates/new", exact: true, component: namedLazy(()=> import("./pages/company/CompanyManageUpdatesNewPage"), "CompanyManageUpdatesNewPage") },
  { path: "/manage-team/updates/:id", exact: true, component: namedLazy(()=> import("./pages/company/CompanyManageUpdatesEditPage"), "CompanyManageUpdatesEditPage") },
  { path: eRoutes["/manage-team/files"], exact: true, component: namedLazy(() => import("./pages/user/CompanySettingsPage"), "CompanySettingsPage") },
  { path: "/manage-team/manageFlows", exact: true, component: namedLazy(() => import("./pages/user/CompanySettingsPage"), "CompanySettingsPage") },
  { path: "/manage-team/links", exact: true, component: namedLazy(() => import("./pages/user/CompanySettingsPage"), "CompanySettingsPage") },
  { path: "/manage-team/hrProfiles", exact: true, component: namedLazy(() => import("./pages/user/CompanySettingsPage"), "CompanySettingsPage") },
  { path: "/manage-team/appearance", exact: true, component: namedLazy(() => import("./pages/user/CompanySettingsPage"), "CompanySettingsPage") },
  { path: eRoutes["/manage-team/billing"], exact: true, component: UserManageBillingInfo },
  { path: "/manage-team/billing-history", exact: true, component: namedLazy(() => import("./pages/user/CompanySettingsPage"), "CompanySettingsPage") },
  { path: "/manage-team/history", exact: true, component: namedLazy(() => import("./pages/user/CompanySettingsPage"), "CompanySettingsPage") },
  { path: "/manage-team/location", exact: true, component: namedLazy(() => import("./pages/user/CompanySettingsPage"), "CompanySettingsPage") },
  { path: eRoutes["/manage-team/sub-companies"], exact: true, component: namedLazy(() => import("./pages/user/CompanySubCompaniesPage"), "CompanySubCompaniesPage") },
  { path: eRoutes["/manage-team/sub-companies/new"], exact: true, component: namedLazy(() => import("./pages/user/CompanySubCompaniesNewPage"), "CompanySubCompaniesNewPage") },
  { path: eRoutes["/manage-team/sub-companies/:id"], exact: true, component: namedLazy(() => import("./pages/user/CompanySubCompaniesShowPage"), "CompanySubCompaniesShowPage") },

  { path: eRoutes["/flow-comments"], exact: true, component: namedLazy(() => import("./pages/user/UsersFlowCommentsIndexPage"), "UsersFlowCommentsIndexPage"), hasSidebar: true },


  // Manager admin pages
  { path: eRoutes["/manager/employees"], hasSidebar: true, component: namedLazy(() => import("./pages/user/ManagerEmployeesIndexPage"), "ManagerEmployeesIndexPage"), exact: true },
  { path: eRoutes["/manager/employees/new"], hasSidebar: true, component: namedLazy(() => import("./pages/user/ManagerEmployeesNewPage"), "ManagerEmployeesNewPage"), exact: true },
  { path: eRoutes["/manager/employees/import"], hasSidebar: true, component: namedLazy(() => import("./pages/user/ManagerEmployeesImportPage"), "ManagerEmployeesImportPage"), exact: true },
  { path: eRoutes["/manager/employees/bulk-archive"], hasSidebar: true, component: namedLazy(() => import("./pages/user/ManagerEmployeesBulkArchivePage"), "ManagerEmployeesBulkArchivePage"), exact: true },
  { path: eRoutes["/manager/employees/:id"], hasSidebar: true, component: namedLazy(()=> import("./pages/user/UserManageEmployeePage/UserManageEmployeePage"), "UserManageEmployeeManagerPage") },
  { path: eRoutes["/manager/users"], hasSidebar: true, component: namedLazy(() => import("./pages/user/ManagerUsersIndexPage"), "ManagerUsersIndexPage"), exact: true },
  { path: eRoutes["/manager/users/new"], hasSidebar: true, component: namedLazy(() => import("./pages/user/ManagerUsersNewPage"), "ManagerUsersNewPage"), exact: true },
  { path: eRoutes["/manager/users/import"], hasSidebar: true, component: namedLazy(() => import("./pages/user/ManagerUsersImportPage"), "ManagerUsersImportPage"), exact: true },
  { path: eRoutes["/manager/users/bulk-archive"], hasSidebar: true, component: namedLazy(() => import("./pages/user/ManagerUsersBulkArchivePage"), "ManagerUsersBulkArchivePage"), exact: true },
  { path: eRoutes["/manager/users/:id"], hasSidebar: true, component: namedLazy(() => import("./pages/user/ManagerUsersShowPage"), "ManagerUsersShowPage"), exact: true },
  //
  { path: eRoutes["/profile"], component: namedLazy(() => import("./pages/user/UserProfilePage"), "UserProfilePage"), hasSidebar: true },
  { path: "/employees/:id/onboarding", component: namedLazy(() => import("./pages/user/EmployeeAppraisalsSetupFlow"), "EmployeeAppraisalsSetupFlow") },
  { path: "/employees/:employeeId/appraisals/:id", component: namedLazy(()=> import("./pages/user/UserManageEmployeeAppraisalsPage"), "UserManageEmployeeAppraisalsPage") },
  { path: "/employees/:employeeId/meetings/:id", component: namedLazy(()=> import("./pages/user/UserManageEmployeeMeetingsPage"), "UserManageEmployeeMeetingsPage") },
  { path: "/manage-hr-profiles/:id", component: namedLazy(()=> import("./pages/user/UserManageHrProfilesEditPage"), "UserManageHrProfilesEditPage") },
  { path: "/files", hasSidebar: true, component: namedLazy(()=> import("./pages/user/UserFileIndexPage"), "UserFileIndexPage") },
  ...(process.env.NODE_ENV === "development"
    ? [{ path: "/dev/file-testing", exact: true, hasSidebar: true, component: namedLazy(() => import("./pages/dev/FileTestingPage"), "FileTestingPage") }]
    : []),
  { path: "/transcript-folders/:id/delete", component: namedLazy(()=> import("./pages/user/UserTranscriptFoldersDeletePage"), "UserTranscriptFoldersDeletePage") },
  { path: "/transcripts/new", component: namedLazy(()=> import("./pages/user/UserTranscriptsNewPage"), "UserTranscriptsNewPage") },
  { path: "/transcripts/:id/delete", component: namedLazy(()=> import("./pages/user/UserTranscriptsDeletePage"), "UserTranscriptsDeletePage") },
  { path: "/transcripts/:id", component: namedLazy(()=> import("./pages/user/UserTranscriptsEditPage"), "UserTranscriptsEditPage") },
  { path: "/transcripts", component: namedLazy(()=> import("./pages/user/UserTranscriptsIndexPage"), "UserTranscriptsIndexPage") },
  { path: eRoutes["/help-videos"], component: namedLazy(()=> import("./pages/user/UserInformationItemsIndexPage"), "UserInformationItemIndexPage") },
  { path: "/updates", component: namedLazy(() => import("./pages/user/UserFeatureHighlightsIndexPage"), "UserFeatureHighlightIndexPage") },
  { path: "/internal-resources", component: namedLazy(() => import("./pages/user/UserCompanyLinksPage"), "UserCompanyLinksPage") },
  { path: routes.knowledgeBase, component: namedLazy(()=> import("./pages/user/UserKnowledgeBasePage"), "UserKnowledgeBasePage") },
  {
    path: "/result-file-requests",
    component: () => <Redirect to="/reminders?pageTab=file-requests" />,
  },
  { path: "/hr-profile", component: namedLazy(() => import("./pages/hrProfile/HrProfilePage"), "HrProfilePage") },

  { path: eRoutes["/reports"], exact: true, hasSidebar: true, component: namedLazy(() => import("./pages/user/ReportsPage"), "ReportsPage") },
  { path: eRoutes["/reports/cases"], hasSidebar: true, component: namedLazy(() => import("./pages/user/UserCaseReportsPage"), "UserCaseReportsPage") },
  { path: eRoutes["/reports/overtime"], hasSidebar: true, component: namedLazy(() => import("./pages/user/UserOvertimeReportsPage"), "UserOvertimeReportsPage") },
  { path: eRoutes["/reports/time-taken"], hasSidebar: true, component: namedLazy(() => import("./pages/user/UserTimeTakenReportsPage"), "UserTimeTakenReportsPage") },
  { path: "/help-requests/:id", component: namedLazy(() => import( "./pages/user/HelpRequests/UserHelpRequestsShowPage"), "UserHelpRequestsShowPage") },
  { path: "/help-requests", component: namedLazy(() => import( "./pages/user/HelpRequests/UserHelpRequestsIndexPage"), "UserHelpRequestsIndexPage") },
  {
    path: routes.rota,
    component: namedLazy(()=> import("./pages/user/UserRotaPage"), "UserRotaPage"),
    noWrapperPadding: true,
    noFooter: true,
    exact: true,
  },
  {
    path: routes.rotaTemplateEdit(":id"),
    component: namedLazy(()=> import("./pages/user/UserRotaTemplatesEditPage"), "UserRotaTemplatesEditPage"),
    noWrapperPadding: true,
  },
  { path: eRoutes["/absences"], component: namedLazy(() => import("./pages/user/UserAbsencesPage"), "UserAbsencesPage") },
  ...(userHrRoutes as any),
  { path: "/logout", exact: true, component: namedLazy(() => import("./pages/session/LogoutPage"), "LogoutPage") },
  employeeInviteAuthenticatedRoute,
  ...(termsRoutes as any),
  ...(anonymousPathRedirects as any),
  { path: "", component: () => <PageNotFoundPage /> },
]

const hrRoutes = [
  { path: "/", exact: true, component: namedLazy(() => import("./pages/hrProfile/HrProfileDashboard"), "HrProfileDashboard") },
  { path: "/reviews", component: namedLazy(() => import("./pages/hrProfile/HrReviewsIndexPage"), "HrReviewsIndexPage") },
  { path: "/completed-work-history", exact: true, component: namedLazy(() => import("./pages/hrProfile/HrWorkHistoryCompletedIndexPage"), "HrWorkHistoryCompletedIndexPage") },
  { path: "/work-history", exact: true, component: namedLazy(() => import("./pages/hrProfile/HrWorkHistoryIndexPage"), "HrWorkHistoryIndexPage") },
  { path: "/completed-work-history/:id", exact: true, component: namedLazy(() => import("./pages/hrProfile/HrWorkHistoryShowPage"), "HrWorkHistoryShowPage") },
  { path: "/work-history/:id", component: namedLazy(() => import("./pages/hrProfile/HrWorkHistoryShowPage"), "HrWorkHistoryShowPage") },
  { path: "/logout", exact: true, component: namedLazy(() => import("./pages/session/LogoutPage"), "LogoutPage") },
  employeeInviteAuthenticatedRoute,
  ...termsRoutes,
  ...anonymousPathRedirects,
  { path: "", component: () => <PageNotFoundPage /> },
]

const adminRoutes = ([
  { path: "/", exact: true, component: namedLazy(()=> import("./pages/admin/HomePage"), "HomePage") },
  { path: "/debug", exact: true, component: namedLazy(()=> import("./pages/admin/DebugPage"), "DebugPage") },
  ...(process.env.NODE_ENV === "development"
    ? [{ path: "/dev/file-testing", exact: true, component: namedLazy(() => import("./pages/dev/FileTestingPage"), "FileTestingPage") }]
    : []),
  { path: "/emails", exact: true, component: namedLazy(()=> import("./pages/admin/EmailsIndexPage"), "EmailsIndexPage") },
  ///// route for rota shift types
  { path: "/rota-shift-types", component: namedLazy(()=> import("./pages/admin/RotaShiftTypes/RotaShiftTypesIndex"), "RotaShiftTypesIndex"), exact: true },
  { path: "/rota-shift-types/new", component: namedLazy(()=> import("./pages/admin/RotaShiftTypes/RotaShiftTypesNewPage"), "RotaShiftTypesNewPage"), exact: true },
  { path: "/rota-shift-types/:id", component: namedLazy(()=> import("./pages/admin/RotaShiftTypes/RotaShiftTypesEditPage"), "RotaShiftTypesEditPage"), exact: true },
  ///// routes for taking flow
  { path: "/case-coaching/:flowId/begin", component: namedLazy(()=> import("./pages/admin/AdminCaseCoachingBeginPage"), "AdminCaseCoachingBeginPage"), exact: true },
  { path: "/case-coaching/:resultId", component: namedLazy(()=> import("./pages/admin/AdminCaseCoachingTakingPage"), "AdminCaseCoachingTakingPage"), exact: true },
  {
    path: "/case-coaching/:id/results",
    component: () => (
      <SkeemFlowProvider>
        <ResultsShowPage />
      </SkeemFlowProvider>
    ),
    exact: true,
  },
  { path: "/flow-groups/:id/show", component: namedLazy(()=> import("./pages/admin/AdminFlowGroupPage"), "AdminFlowGroupPage") },
  /////
  {
    path: eRoutes["/questions/new"],
    component: () => (
      <SkeemFlowProvider>
        <QuestionsNewPage />
      </SkeemFlowProvider>
    ),
  },
  { path: eRoutes["/questions/:id/delete"], component: namedLazy(()=> import("./pages/admin/QuestionsDeletePage"), "QuestionsDeletePage") },
  {
    path: eRoutes["/questions/:id"],
    component: () => (
      <SkeemFlowProvider>
        <QuestionsEditPage />
      </SkeemFlowProvider>
    ),
  },
  { path: eRoutes["/questions"], component: namedLazy(()=> import("./pages/admin/QuestionsIndexPage"), "QuestionsIndexPage") },
  { path: eRoutes["/question-sets"], component: namedLazy(() => import("./pages/admin/AdminQuestionSetsIndexPage"), "AdminQuestionSetsIndexPage") },
  { path: eRoutes["/question-sets/new"], component: namedLazy(() => import("./pages/admin/AdminQuestionSetsNewPage"), "AdminQuestionSetsNewPage") },
  { path: eRoutes["/question-sets/:id"], component: namedLazy(() => import("./pages/admin/AdminQuestionSetsShowPage"), "AdminQuestionSetsShowPage") },
  { path: "/sections/new", component: namedLazy(()=> import("./pages/admin/SectionsNewPage"), "SectionsNewPage") },
  { path: "/sections/:id/delete", component: namedLazy(()=> import("./pages/admin/SectionsDeletePage"), "SectionsDeletePage") },
  { path: "/sections/:id", component: namedLazy(()=> import("./pages/admin/SectionsEditPage"), "SectionsEditPage") },
  { path: "/sections", component: namedLazy(()=> import("./pages/admin/SectionsIndexPage"), "SectionsIndexPage") },
  { path: "/flow-groups/new", component: namedLazy(()=> import("./pages/admin/FlowGroupsNewPage"), "FlowGroupsNewPage") },
  { path: "/flow-groups/:id/delete", component: namedLazy(()=> import("./pages/admin/FlowGroupsDeletePage"), "FlowGroupsDeletePage") },
  { path: "/flow-groups/:id", component: namedLazy(()=> import("./pages/admin/FlowGroupsEditPage"), "FlowGroupsEditPage") },
  { path: "/flows/new", component: namedLazy(()=> import("./pages/admin/FlowsNewPage"), "FlowsNewPage") },
  { path: eRoutes["/flows/report"], component: namedLazy(()=> import("./pages/admin/AdminFlowsReportPage"), "AdminFlowsReportPage") },
  { path: "/flows/sort", component: namedLazy(()=> import("./pages/admin/FlowGroupsSortPage"), "FlowGroupsSortPage") },
  { path: "/flows/:id/delete", component: namedLazy(()=> import("./pages/admin/FlowsDeletePage"), "FlowsDeletePage") },
  routesWithWrapper(
    eRoutes["/flows/:id"],
    namedLazy(() => import("./pages/admin/AdminFlowsPageWrapper"), "AdminFlowsPageWrapper"),
    [
      { path: eRoutes["/flows/:id"], component: namedLazy(() => import("./pages/admin/AdminFlowsEditDetailsPage"), "AdminFlowsEditDetailsPage") },
      { path: eRoutes["/flows/:id/steps"], component: namedLazy(() => import("./pages/admin/AdminFlowsEditStepsPage"), "AdminFlowsEditStepsPage") },
      { path: eRoutes["/flows/:id/draft"], component: namedLazy(() => import("./pages/admin/AdminFlowsEditDraftPage"), "AdminFlowsEditDraftPage") },
      { path: eRoutes["/flows/:id/comments"], component: namedLazy(() => import("./pages/admin/AdminFlowsEditCommentsPage"), "AdminFlowsEditCommentsPage") },
      { path: eRoutes["/flows/:id/results"], component: namedLazy(() => import("./pages/admin/AdminFlowsEditResultsPage"), "AdminFlowsEditResultsPage") },
    ]
  ),
  { path: eRoutes["/admin/emails"], component: namedLazy(() => import("./pages/admin/AdminEmailsIndexPage"), "AdminEmailsIndexPage") },
  { path: eRoutes["/admin/emails/:id"], component: namedLazy(() => import("./pages/admin/AdminEmailsShowPage"), "AdminEmailsShowPage") },

  { path: "/flows", component: namedLazy(()=> import("./pages/admin/FlowsIndexPage"), "FlowsIndexPage") },
  { path: "/risks/new", component: namedLazy(()=> import("./pages/admin/RisksNewPage"), "RisksNewPage") },
  { path: "/risks/:id/delete", component: namedLazy(()=> import("./pages/admin/RisksDeletePage"), "RisksDeletePage") },
  { path: "/risks/:id", component: namedLazy(()=> import("./pages/admin/RisksEditPage"), "RisksEditPage") },
  { path: "/risks", component: namedLazy(()=> import("./pages/admin/RisksIndexPage"), "RisksIndexPage") },
  { path: "/action-tags/new", component: namedLazy(()=> import("./pages/admin/ActionTagsNewPage"), "ActionTagsNewPage") },
  { path: "/action-tags/:id/delete", component: namedLazy(()=> import("./pages/admin/ActionTagsDeletePage"), "ActionTagsDeletePage") },
  { path: "/action-tags/:id", component: namedLazy(()=> import("./pages/admin/ActionTagsEditPage"), "ActionTagsEditPage") },
  { path: "/action-tags", component: namedLazy(()=> import("./pages/admin/ActionTagsIndexPage"), "ActionTagsIndexPage") },
  { path: "/information-items/new", component: namedLazy(()=> import("./pages/admin/InformationItemsNewPage"), "InformationItemsNewPage") },
  { path: "/information-items/sort", component: namedLazy(()=> import("./pages/admin/InformationItemsSortPage"), "InformationItemsSortPage") },
  { path: "/information-items/:id/delete", component: namedLazy(()=> import("./pages/admin/InformationItemsDeletePage"), "InformationItemsDeletePage") },
  { path: "/information-items/:id", component: namedLazy(()=> import("./pages/admin/InformationItemsEditPage"), "InformationItemsEditPage") },
  { path: "/information-items", component: namedLazy(()=> import("./pages/admin/InformationItemsIndexPage"), "InformationItemsIndexPage") },
  { path: "/updates/new", component: namedLazy(()=> import("./pages/admin/FeatureHighlightsNewPage"), "FeatureHighlightsNewPage") },
  { path: "/updates/:id/delete", component: namedLazy(()=> import("./pages/admin/FeatureHighlightsDeletePage"), "FeatureHighlightsDeletePage") },
  { path: "/updates/:id", component: namedLazy(()=> import("./pages/admin/FeatureHighlightsEditPage"), "FeatureHighlightsEditPage") },
  { path: "/updates", component: namedLazy(()=> import("./pages/admin/FeatureHighlightsIndexPage"), "FeatureHighlightsIndexPage") },
  { path: "/outcomes/new", component: namedLazy(()=> import("./pages/admin/OutcomesNewPage"), "OutcomesNewPage") },
  { path: "/outcomes/:id/delete", component: namedLazy(()=> import("./pages/admin/OutcomesDeletePage"), "OutcomesDeletePage") },
  { path: "/outcomes/:id", component: namedLazy(()=> import("./pages/admin/OutcomesEditPage"), "OutcomesEditPage") },
  { path: "/outcomes", component: namedLazy(()=> import("./pages/admin/OutcomesIndexPage"), "OutcomesIndexPage") },
  { path: eRoutes["/progress-tags"], component: namedLazy(() => import("./pages/admin/ProgressTagsIndexPage"), "ProgressTagsIndexPage"), exact: true },
  { path: eRoutes["/progress-tags/new"], component: namedLazy(() => import("./pages/admin/ProgressTagsNewPage"), "ProgressTagsNewPage"), exact: true },
  { path: eRoutes["/progress-tags/:id"], component: namedLazy(() => import("./pages/admin/ProgressTagsShowPage"), "ProgressTagsShowPage"), exact: true },
  { path: "/flow-tags", component: namedLazy(()=> import("./pages/admin/FlowTagsIndexPage"), "FlowTagsIndexPage"), exact: true },
  { path: "/flow-tags/new", component: namedLazy(()=> import("./pages/admin/FlowTagsNewPage"), "FlowTagsNewPage"), exact: true },
  { path: "/flow-tags/:id", component: namedLazy(()=> import("./pages/admin/FlowTagsEditPage"), "FlowTagsEditPage"), exact: true },
  { path: "/flow-tags/:id/archive", component: namedLazy(()=> import("./pages/admin/FlowTagsArchivePage"), "FlowTagsArchivePage"), exact: true },
  {
    path: "/help/new",
    component: () => (
      <SkeemFlowProvider>
        <ResolutionsNewPage />
      </SkeemFlowProvider>
    ),
  },
  { path: "/help/:id/delete", component: namedLazy(()=> import("./pages/admin/ResolutionsDeletePage"), "ResolutionsDeletePage") },
  {
    path: "/help/:id",
    component: () => (
      <SkeemFlowProvider>
        <ResolutionsEditPage />
      </SkeemFlowProvider>
    ),
  },
  { path: "/help", component: namedLazy(()=> import("./pages/admin/ResolutionsIndexPage"), "ResolutionsIndexPage") },
  { path: "/companies/new", component: namedLazy(() => import("./pages/admin/AdminCompaniesNewPage"), "AdminCompaniesNewPage") },
  routesWithWrapper(eRoutes["/companies/:id"], AdminCompaniesGlobalStateProvider, [
    { path: "/companies/:id", component: namedLazy(()=> import("./pages/admin/AdminCompaniesShowPage"), "AdminCompaniesShowPage") },
    { path: eRoutes["/companies/:id/teams"], component: namedLazy(()=> import("./pages/company/CompanyPeoplesTeamsIndex"), "CompanyPeoplesTeamsIndex") },
    { path: "/companies/:id/teams/import", component: namedLazy(()=> import("./forms/CompanyTeamsImportPage"), "CompanyTeamsImportPage") },
    routesWithWrapper(
      eRoutes["/companies/:companyId/teams/:id"],
      namedLazy(() => import("./pages/user/UserManageTeamShowPageWrapper"), "UserManageTeamShowPageWrapper"),
      [
        { path: eRoutes["/companies/:companyId/teams/:id"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamShowPage") },
        { path: eRoutes["/companies/:companyId/teams/:id/employees"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamEmployeesPage") },
        { path: eRoutes["/companies/:companyId/teams/:id/employees/import"], component: namedLazy(() => import("./pages/user/UserManageTeamShowImportPage"), "UserManageTeamShowImportPage") },
        { path: eRoutes["/companies/:companyId/teams/:id/users"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamUsersPage") },
        { path: eRoutes["/companies/:companyId/teams/:id/cases"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamCasesPage") },
        { path: eRoutes["/companies/:companyId/teams/:id/edit"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamEditPage") },
        { path: eRoutes["/companies/:companyId/teams/:id/delete"], component: namedLazy(() => import("./pages/user/UserManageTeamShowPage"), "UserTeamDeletePage") },
      ]
    ),

    { path: eRoutes["/companies/:id/employees"], component: namedLazy(() => import("./pages/admin/AdminCompanyEmployeeIndexPage"), "AdminCompanyEmployeeIndexPage") },
    { path: "/companies/:id/users", component: namedLazy(() => import("./pages/admin/AdminCompanyUsersIndexPage"), "AdminCompanyUsersIndexPage") },
    { path: eRoutes["/companies/:id/users/import"], component: namedLazy(() => import("./pages/admin/AdminCompanyUsersImportPage"), "AdminCompanyUsersImportPage") },
    { path: eRoutes["/companies/:id/users/new"], component: namedLazy(() => import("./pages/admin/AdminCompanyUsersNewPage"), "AdminCompanyUsersNewPage") },
    { path: eRoutes["/companies/:id/users/bulk-archive"], component: namedLazy(() => import("./pages/admin/AdminCompanyUsersBulkArchivePage"), "AdminCompanyUsersBulkArchivePage") },
    { path: eRoutes["/companies/:companyId/users/:id"], component: namedLazy(() => import("./pages/admin/AdminCompanyUsersShowPage"), "AdminCompanyUsersShowPage") },
    { path: eRoutes["/companies/:id/employees/new"], component: namedLazy(() => import("./pages/admin/AdminCompanyEmployeeNewPage"), "AdminCompanyEmployeeNewPage") },
    { path: eRoutes["/companies/:id/employees/import"], component: namedLazy(() => import("./pages/admin/AdminCompanyEmployeeImportPage"), "AdminCompanyEmployeeImportPage") },
    { path: eRoutes["/companies/:id/employees/bulk-archive"], component: namedLazy(() => import("./pages/admin/AdminCompanyEmployeesBulkArchivePage"), "AdminCompanyEmployeesBulkArchivePage") },
    { path: eRoutes["/companies/:companyId/employees/:id"], component: namedLazy(() => import("./pages/admin/AdminCompanyEmployeeShowPage"), "AdminCompanyEmployeeShowPage") },
    { path: eRoutes["/companies/:id/sub-companies"], component: namedLazy(() => import("./pages/admin/AdminCompanySubCompaniesIndexPage"), "AdminCompanySubCompaniesIndexPage") },
    { path: eRoutes["/companies/:id/sub-companies/new"], component: namedLazy(() => import("./pages/admin/AdminCompanySubCompaniesNewPage"), "AdminCompanySubCompaniesNewPage") },
    { path: eRoutes["/companies/:id/sub-companies/:subCompanyId"], component: namedLazy(() => import("./pages/user/CompanySubCompaniesShowPage"), "CompanySubCompaniesShowPage") },
    { path: eRoutes["/companies/:id/settings"], component: namedLazy(() => import("./pages/admin/AdminCompanySettingsPage"), "AdminCompanySettingsPage") },
    { path: "/companies/:id/sso", component: namedLazy(() => import("./pages/admin/AdminCompanySsoPage"), "AdminCompanySsoPage") },
    { path: eRoutes["/companies/:id/alerts"], component: namedLazy(() => import("./pages/admin/AdminCompanyAlertsPage"), "AdminCompanyAlertsPage") },
    { path: "/companies/:id/help-requests", component: namedLazy(() => import("./pages/company/CompanyManageHelpRequestsIndexPage"), "AdminCompanyManageHelpRequestsIndexPage") },
    { path: "/companies/:companyId/help-requests/:id", component: namedLazy(() => import("./pages/company/CompanyManageHelpRequestsShowPage"), "AdminCompanyManageHelpRequestsShowPage") },
    { path: eRoutes["/companies/:id/files"], component: namedLazy(() => import("./pages/admin/AdminCompanyFilesPage"), "AdminCompanyFilesPage") },
    { path: eRoutes["/companies/:id/manage-flows"], component: namedLazy(() => import("./pages/admin/AdminCompanyManageFlowsPage"), "AdminCompanyManageFlowsPage") },
    { path: eRoutes["/companies/:id/details"], component: namedLazy(() => import("./pages/admin/AdminCompanyDetailsPage"), "AdminCompanyDetailsPage") },
    { path: eRoutes["/companies/:id/plan"], component: namedLazy(() => import("./pages/admin/AdminCompanyPlanPage"), "AdminCompanyPlanPage") },
    { path: eRoutes["/companies/:id/subscription"], component: namedLazy(() => import("./pages/admin/AdminCompanySubscriptionPage"), "AdminCompanySubscriptionPage") },
    { path: eRoutes["/companies/:id/billing"], component: namedLazy(() => import("./pages/admin/AdminCompanyBillingPage"), "AdminCompanyBillingPage") },
    { path: eRoutes["/companies/:id/links"], component: namedLazy(() => import("./pages/admin/AdminCompanyLinksPage"), "AdminCompanyLinksPage") },
    { path: eRoutes["/companies/:id/whitelabel"], component: namedLazy(() => import("./pages/admin/AdminCompanyWhiteLabelPage"), "AdminCompanyWhiteLabelPage") },
    { path: eRoutes["/companies/:id/history"], component: namedLazy(() => import("./pages/admin/AdminCompanyHistoryPage"), "AdminCompanyHistoryPage") },
    { path: eRoutes["/companies/:id/locations"], component: namedLazy(() => import("./pages/admin/AdminCompanyLocationsPage"), "AdminCompanyLocationsPage") },
    { path: eRoutes["/companies/:id/archive"], component: namedLazy(() => import("./pages/admin/AdminCompanyArchivePage"), "AdminCompanyArchivePage") },
    { path: "/companies/:id/employee-tags", component: namedLazy(() => import("./pages/EmployeeTags/AdminEmployeeTagsIndexPage"), "AdminEmployeeTagsIndex") },
    { path: "/companies/:id/departments", component: namedLazy(()=> import("./pages/company/CompanyManageDepartmentsPage"), "CompanyManageDepartmentsPage") },
    { path: "/companies/:id/tasks", component: namedLazy(()=> import("./pages/company/CompanyRemindersPage"), "CompanyRemindersPage") },
    { path: eRoutes["/companies/:id/fourth"], exact: true, component: namedLazy(() => import("./pages/admin/AdminCompanyFourthPage"), "AdminCompanyFourthPage") },
    { path: eRoutes["/companies/:id/itrent"], exact: true, component: namedLazy(() => import("./pages/admin/AdminCompanyITrentPage"), "AdminCompanyITrentPage") },
  ]),
  { path: "/companies", component: namedLazy(() => import("./pages/admin/AdminCompaniesIndexPage"), "AdminCompaniesIndexPage") },
  { path: "/files/new", component: namedLazy(()=> import("./pages/admin/FilesNewPage"), "FilesNewPage") },
  { path: "/files/:id/delete", component: namedLazy(()=> import("./pages/admin/FilesDeletePage"), "FilesDeletePage") },
  routesWithWrapper(
    eRoutes["/files/:id"],
    namedLazy(() => import("./pages/admin/FilesShowPageWrapper"), "FilesShowPageWrapper"),
    [
      { path: eRoutes["/files/:id"], component: namedLazy(() => import("./pages/admin/FilesShowPage"), "FilesShowPage") },
      { path: eRoutes["/files/:id/edit"], component: namedLazy(() => import("./pages/admin/FilesEditPage"), "FilesEditPage") },
    ],
  ),
  { path: "/files", component: namedLazy(()=> import("./pages/admin/FilesIndexPage"), "FilesIndexPage") },
  { path: "/folders/:id", component: namedLazy(()=> import("./pages/admin/FileFoldersEditPage"), "FileFoldersEditPage") },
  { path: "/question-folders/:id/delete", component: namedLazy(()=> import("./pages/admin/QuestionFoldersDeletePage"), "QuestionFoldersDeletePage") },
  { path: "/outcome-folders/:id/delete", component: namedLazy(()=> import("./pages/admin/OutcomeFoldersDeletePage"), "OutcomeFoldersDeletePage") },
  { path: "/help-folders/:id/delete", component: namedLazy(()=> import("./pages/admin/ResolutionFoldersDeletePage"), "ResolutionFoldersDeletePage") },
  { path: "/users/:id/delete", component: namedLazy(()=> import("./pages/admin/UsersDeletePage"), "UsersDeletePage") },
  { path: "/admin-users/new", component: namedLazy(()=> import("./pages/admin/AdminUsers/AdminUsersNewPage"), "AdminUsersNewPage") },
  { path: "/admin-users/:id/delete", component: namedLazy(()=> import("./pages/admin/AdminUsers/AdminUsersDeletePage"), "AdminUsersDeletePage") },
  { path: "/admin-users/:id", component: namedLazy(()=> import("./pages/admin/AdminUsers/AdminUsersEditPage"), "AdminUsersEditPage") },
  { path: "/admin-users", component: namedLazy(()=> import("./pages/admin/AdminUsers/AdminUsersIndexPage"), "AdminUsersIndexPage") },
  { path: "/help-requests/:id/delete", component: namedLazy(()=> import("./pages/admin/HelpRequestsDeletePage"), "HelpRequestsDeletePage") },
  { path: "/help-requests/:id", component: namedLazy(()=> import("./pages/admin/HelpRequestsEditPage"), "HelpRequestsEditPage") },
  { path: "/help-requests", component: namedLazy(()=> import("./pages/admin/HelpRequestsIndexPage"), "HelpRequestsIndexPage") },
  { path: "/logout", component: namedLazy(() => import("./pages/session/LogoutPage"), "LogoutPage") },
  { path: "/archive", component: namedLazy(()=> import("./pages/admin/ArchiveSearchPage"), "ArchiveSearchPage") },
  { path: "/billing/plans/new", component: namedLazy(()=> import("./pages/admin/PlansNewPage"), "PlansNewPage") },
  { path: "/billing/plans/:id/delete", component: namedLazy(()=> import("./pages/admin/PlansDeletePage"), "PlansDeletePage") },
  { path: "/billing/plans/:id", component: namedLazy(()=> import("./pages/admin/PlansEditPage"), "PlansEditPage") },
  { path: "/billing/plans", component: namedLazy(()=> import("./pages/admin/PlansIndexPage"), "PlansIndexPage") },
  { path: "/billing/per-user-plans/new", component: namedLazy(() => import( "./pages/admin/PerUserPlansNewPage"), "PerUserPlansNewPage") },
  { path: "/billing/per-user-plans/:id/delete", component: namedLazy(() => import( "./pages/admin/PerUserPlansDeletePage"), "PerUserPlansDeletePage") },
  { path: "/billing/per-user-plans/:id", component: namedLazy(() => import( "./pages/admin/PerUserPlansEditPage"), "PerUserPlansEditPage") },
  { path: "/billing/per-user-plans", component: namedLazy(() => import( "./pages/admin/PerUserPlansIndexPage"), "PerUserPlansIndexPage") },
  { path: "/billing/payment-history", component: namedLazy(()=> import("./pages/admin/BillingPaymentHistoryPage"), "BillingPaymentHistoryPage") },
  { path: "/billing/subscriptions", component: namedLazy(()=> import("./pages/admin/BillingSubscriptionsPage"), "BillingSubscriptionsPage") },
  { path: "/billing", component: namedLazy(()=> import("./pages/admin/BillingPage"), "BillingPage") },
  { path: "/employees/:id/onboarding", component: () => <EmployeeAppraisalsSetupFlow isAdmin /> },
  { path: "/employees/:employeeId/appraisals/:id", component: namedLazy(()=> import("./pages/user/UserManageEmployeeAppraisalsPage"), "UserManageEmployeeAppraisalsPage") },
  { path: "/employees/:employeeId/meetings/:id", component: namedLazy(()=> import("./pages/user/UserManageEmployeeMeetingsPage"), "UserManageEmployeeMeetingsPage") },
  { path: eRoutes["/hr-profiles"], component: namedLazy(()=> import("./pages/admin/HrProfilesIndexPage"), "HrProfilesIndexPage") },
  { path: eRoutes["/hr-profiles/sort"], component: namedLazy(()=> import("./pages/admin/HrProfilesSortPage"), "HrProfilesSortPage") },
  { path: eRoutes["/hr-profiles/new"], component: namedLazy(()=> import("./pages/admin/HrProfilesNewPage"), "HrProfilesNewPage") },
  { path: eRoutes["/hr-profiles/:id/delete"], component: namedLazy(()=> import("./pages/admin/HrProfilesDeletePage"), "HrProfilesDeletePage") },
  { path: eRoutes["/hr-profiles/:id"], component: namedLazy(()=> import("./pages/admin/HrProfilesEditPage"), "HrProfilesEditPage") },
  { path: "/hr-jobs/:id", component: namedLazy(()=> import("./pages/admin/HrJobShowPage"), "HrJobShowPage") },
  { path: "/reviews/:id", component: namedLazy(()=> import("./pages/admin/ReviewsShowPage"), "ReviewsShowPage") },

  { path: "/admin/web", component: namedLazy(() => import( "./pages/admin/AdminWebPage"), "AdminWebPage") },
  { path: "/admin/web/enquiries", component: namedLazy(() => import( "./pages/admin/AdminWebEnquiriesPage"), "AdminWebEnquiriesPage") },
  { path: "/admin/web/reviews", component: namedLazy(() => import( "./pages/admin/AdminWebReviewsPage"), "AdminWebReviewsPage") },
  { path: "/admin/web/reviews/new", component: namedLazy(() => import( "./pages/admin/AdminWebReviewsNewPage"), "AdminWebReviewsNewPage") },
  { path: "/admin/web/reviews/:id/edit", component: namedLazy(() => import( "./pages/admin/AdminWebReviewsEditPage"), "AdminWebReviewsEditPage") },
  { path: "/admin/web/case-studies", component: namedLazy(() => import( "./pages/admin/AdminWebCaseStudiesPage"), "AdminWebCaseStudiesPage") },
  { path: "/admin/web/case-studies/new", component: namedLazy(() => import( "./pages/admin/AdminWebCaseStudiesNewPage"), "AdminWebCaseStudiesNewPage") },
  { path: "/admin/web/case-studies/:id/edit", component: namedLazy(() => import( "./pages/admin/AdminWebCaseStudiesEditPage"), "AdminWebCaseStudiesEditPage") },
  { path: "/admin/web/blogs", component: namedLazy(() => import( "./pages/admin/AdminWebBlogsPage"), "AdminWebBlogsPage") },
  { path: "/admin/web/blogs/new", component: namedLazy(() => import( "./pages/admin/AdminWebBlogsNewPage"), "AdminWebBlogsNewPage") },
  { path: "/admin/web/blogs/:id/edit", component: namedLazy(() => import( "./pages/admin/AdminWebBlogsEditPage"), "AdminWebBlogsEditPage") },

  { path: "/admin/web/faqs", component: namedLazy(() => import( "./pages/admin/AdminWebFaqsPage"), "AdminWebFaqsPage") },
  { path: "/admin/web/faqs/new", component: namedLazy(() => import( "./pages/admin/AdminWebFaqsNewPage"), "AdminWebFaqsNewPage") },
  { path: "/admin/web/faqs/:id/edit", component: namedLazy(() => import( "./pages/admin/AdminWebFaqsEditPage"), "AdminWebFaqsEditPage") },

  ...termsRoutes,
  employeeInviteAuthenticatedRoute,
  ...anonymousPathRedirects,
  { path: "*", component: () => <PageNotFoundPage /> },
] as IRoute[]).map(route => ({ ...route, exact: route.exact === false ? false : true }))

const employeeRoutes: IRoute[] = [
  { path: eRoutes["/"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesDashboardPage"), "EmployeesDashboardPage") },
  { path: eRoutes["/flows/new"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesFlowsNewPage"), "EmployeesFlowsNewPage") },
  { path: eRoutes["/flows/new/:id"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesFlowsIntroPage"), "EmployeesFlowsIntroPage") },
  { path: eRoutes["/results"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesResultsIndexPage"), "EmployeesResultsIndexPage") },
  { path: eRoutes["/case-coaching/:id/results"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesResultsShowPage"), "EmployeesResultsShowPage") },
  { path: eRoutes["/case-coaching/:resultId"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesResultsTakingPage"), "EmployeesResultsTakingPage") },
  { path: eRoutes["/files"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesFilesPage"), "EmployeesFilesPage") },
  { path: eRoutes["/my-rota"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesMyRotaPage"), "EmployeesMyRotaPage") },
  { path: eRoutes["/tasks"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesTasksPage"), "EmployeesTasksPage") },
  { path: eRoutes["/holiday-requests"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesHolidayRequestsPage"), "EmployeesHolidayRequestsPage") },
  { path: eRoutes["/sickness-logs"], exact: true, component: namedLazy(() => import("./pages/employees/EmployeesSicknessLogsPage"), "EmployeesSicknessLogsPage") },
  { path: "/logout", exact: true, component: namedLazy(() => import("./pages/session/LogoutPage"), "LogoutPage") },
  employeeInviteAuthenticatedRoute,
  ...termsRoutes,
  ...anonymousPathRedirects,
  { path: "", component: () => <PageNotFoundPage /> },
]

/* eslint-enable prettier/prettier */

export const RouterSessionContext = React.createContext({
  fetchMeAndSetRoles: async () => {},
  roles: ["anonymous"],
})

export function Router() {
  const skeem = useSkeem()
  const location = useLocation()
  const hasSeenSessionPagesRef = useRef(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [roles, setRoles] = useState(["anonymous"])
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState(false)
  const [showKickedModal, setShowKickedModal] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)

  const [currentCompany, setCurrentCompany] = useState<IRecord<
    "companies",
    "isAdminCompany"
  > | null>(null)

  useAnalyticsPageViews(isLoaded, isAuthenticated)

  useAsyncEffect(async () => {
    try {
      await fetchMeAndSetRoles()
      setIsLoaded(true)

      return skeem.on("session-change", fetchMeAndSetRoles)
    } catch (error) {
      console.error("CRITICAL", error)
      setError(true)
    }
  }, [])

  const shareRotaRoute = matchPath(window.location.pathname, { exact: true, path: "/share/rota" })

  async function fetchMeAndSetRoles() {
    const me = await skeem.me()
    setIsAuthenticated(me.authenticated)
    setRoles(me.roles)

    if (location.pathname === "/is-logged-in") {
      return
    }

    if (me.authenticated) {
      const currentCompany = await skeem.fetch2<IRecord<
        "companies",
        "isAdminCompany" | "archived"
      > | null>({
        first: {
          name: "companies",
          attributes: ["isAdminCompany", "archived"],
          matches: { users: { some: { id: me.id } } },
        },
      })
      assert(currentCompany, "No current company found")
      setCurrentCompany(currentCompany)

      if (currentCompany.archived) {
        await skeem.logout()
        window.location.reload()
        return
      }

      // If current (stored) company was revoked from user's access, auto-switch to first accessible (admins can stay in any company)
      const isAdmin = me.roles.includes("admin")
      if (!isAdmin) {
        const meWithCompany = await skeem.fetchMe<{ company: { id: string } }>(["company"])
        const storedCompanyId = meWithCompany.company?.id
        if (storedCompanyId) {
          const accessibleCompanies = await skeem.fetch2<IRecord<"companies">[]>({
            model: {
              name: "companies",
              filter: {
                and: [
                  { matches: { accessibleUsers: { some: { id: { session: true } } } } },
                  { matches: { archived: false } },
                ],
              },
              sort: { by: "name", direction: "asc" },
            },
          })
          const accessibleIds = accessibleCompanies.map(c => c.id)
          if (!accessibleIds.includes(storedCompanyId)) {
            const targetId = accessibleIds[0]
            if (targetId) {
              await switchCompany(targetId)
              return
            }
          }
        }
      }
    }
  }

  if (location.pathname === "/logout") {
    return <LogoutPage />
  }

  if (location.pathname === "/is-logged-in") {
    return <IsLoggedInPage isAuthenticated={isAuthenticated} isLoaded={isLoaded || error} />
  }

  if (error) {
    return <ErrorSplashScreen />
  } else if (!isLoaded) {
    return <SplashScreen />
  } else if (shareRotaRoute) {
    return (
      <RotaWrapper>
        {renderRoutes([
          {
            path: "/share/rota",
            component: namedLazy(() => import("./pages/user/UserRotaPage"), "AnonRotaPage"),
          },
        ])}
      </RotaWrapper>
    )
  } else if (!isAuthenticated) {
    hasSeenSessionPagesRef.current = true
    const matchingRoutes = anonymousRoutes.find(route =>
      matchPath(location.pathname, {
        path: route.path,
        exact: route.exact,
      }),
    )
    return (
      <AnonymousGlobalStateProvider>
        <SessionWrapper cta={matchingRoutes?.cta}>
          <ManagedModalProvider />
          <AutoLogoutWhenKicked.Modal
            open={showKickedModal}
            onClose={() => setShowKickedModal(false)}
          />
          <ErrorBoundary
            renderOnError={() => (
              <div
                css={css`
                  background: white;
                  padding: 48px 16px;
                  border-radius: 8px;
                `}
              >
                <TextGroupLarge
                  theme={{ heading: { align: "center" }, textBlock: { align: "center" } }}
                  heading="Something has gone wrong."
                  body="Please try again and if this issue persists, contact support."
                />
              </div>
            )}
          >
            {renderRoutes(anonymousRoutes as any)}
          </ErrorBoundary>
        </SessionWrapper>
      </AnonymousGlobalStateProvider>
    )
  } else {
    if (currentCompany?.isAdminCompany) {
      return (
        <GlobalStateProvider>
          <BillingStatusProvider>
            <ManagedModalProvider />
            <RouterSessionContext.Provider value={{ fetchMeAndSetRoles, roles }}>
              <AutoLogoutWhenKicked onAutoLogout={() => setShowKickedModal(true)} />
              <ResultPdfGenerationStatus />
              <AdminSiteWrapper hideAnimation={hasSeenSessionPagesRef.current}>
                <ScrollToTopOnRouteChange />
                <RequirePasswordUpdate />
                <ErrorBoundary>{renderRoutes(adminRoutes as any)}</ErrorBoundary>
              </AdminSiteWrapper>
            </RouterSessionContext.Provider>
          </BillingStatusProvider>
        </GlobalStateProvider>
      )
    } else if (roles.includes("hrProfessional")) {
      const matchingRoutes = employeeRoutes.find(route =>
        matchPath(location.pathname, {
          path: route.path,
          exact: route.exact,
        }),
      )

      return (
        <GlobalStateProvider>
          <ManagedModalProvider />
          <SmartlookInstaller />
          <RouterSessionContext.Provider value={{ fetchMeAndSetRoles, roles }}>
            <AutoLogoutWhenKicked onAutoLogout={() => setShowKickedModal(true)} />
            <UserSiteWrapper
              noPadding={matchingRoutes?.noWrapperPadding}
              noFooter={matchingRoutes?.noFooter}
              hasSidebar={matchingRoutes?.hasSidebar}
            >
              <ScrollToTopOnRouteChange />
              <SwitchBackAfterAdminLoginAsUserButton />
              <RequirePasswordUpdate />
              <ErrorBoundary>{renderRoutes(hrRoutes)}</ErrorBoundary>
            </UserSiteWrapper>
          </RouterSessionContext.Provider>
        </GlobalStateProvider>
      )
    } else if (roles.includes("employee")) {
      const matchingRoutes = employeeRoutes.find(route =>
        matchPath(location.pathname, {
          path: route.path,
          exact: route.exact,
        }),
      )

      return (
        <GlobalStateProvider>
          <ManagedModalProvider />
          <SmartlookInstaller />
          <RouterSessionContext.Provider value={{ fetchMeAndSetRoles, roles }}>
            <AutoLogoutWhenKicked onAutoLogout={() => setShowKickedModal(true)} />
            <AuthorizationHeartbeat />
            <ActivityHeartbeat />
            <TermsAndConditionsPopup onAcceptTerms={() => setTermsAccepted(true)} />
            <UserCanSeeHrAreas />
            <CompanyThemeLoader>
              <NullErrorBoundary>
                <ResultPdfGenerationStatus />
              </NullErrorBoundary>
              <UserSiteWrapper
                noPadding={matchingRoutes?.noWrapperPadding}
                noFooter={matchingRoutes?.noFooter}
                hasSidebar={matchingRoutes?.hasSidebar}
              >
                <BillingStatusProvider>
                  <ScrollToTopOnRouteChange />
                  <SwitchBackAfterAdminLoginAsUserButton />
                  <RequirePasswordUpdate />
                  <AutoBlockUnsubscribed />
                  <ErrorBoundary>
                    {renderRoutes(employeeRoutes as any, { termsAccepted })}
                  </ErrorBoundary>
                </BillingStatusProvider>
              </UserSiteWrapper>
            </CompanyThemeLoader>
          </RouterSessionContext.Provider>
        </GlobalStateProvider>
      )
    } else {
      const matchingRoutes = userRoutes.find(route =>
        matchPath(location.pathname, {
          path: route.path,
          exact: route.exact,
        }),
      )
      return (
        <GlobalStateProvider>
          <ManagedModalProvider />
          <SmartlookInstaller />
          <RouterSessionContext.Provider value={{ fetchMeAndSetRoles, roles }}>
            <AutoLogoutWhenKicked onAutoLogout={() => setShowKickedModal(true)} />
            <AuthorizationHeartbeat />
            <ActivityHeartbeat />
            <TermsAndConditionsPopup onAcceptTerms={() => setTermsAccepted(true)} />
            <UserCanSeeHrAreas />
            <CompanyThemeLoader>
              <NullErrorBoundary>
                <ResultPdfGenerationStatus />
              </NullErrorBoundary>
              <UserSiteWrapper
                noPadding={matchingRoutes?.noWrapperPadding}
                noFooter={matchingRoutes?.noFooter}
                hasSidebar={matchingRoutes?.hasSidebar}
              >
                <BillingStatusProvider>
                  <ScrollToTopOnRouteChange />
                  <SwitchBackAfterAdminLoginAsUserButton />
                  <RequirePasswordUpdate />
                  <AutoBlockUnsubscribed />
                  <ErrorBoundary>
                    {renderRoutes(userRoutes as any, { termsAccepted })}
                  </ErrorBoundary>
                </BillingStatusProvider>
              </UserSiteWrapper>
            </CompanyThemeLoader>
          </RouterSessionContext.Provider>
        </GlobalStateProvider>
      )
    }
  }
}

function AutoBlockUnsubscribed() {
  const alerts = useAlert()
  const skeem = useSkeem()
  const billingStatus = useBillingStatus()
  const location = useLocation()
  const history = useHistory()
  const globalState = useGlobalState()

  const allowedRoutes = [
    "/",
    "/manage-team",
    eRoutes["/help-videos"],
    "/updates",
    "/logout",
    "/privacy-policy",
    "/terms-and-conditions",
    "/eula",
    "/saas",
  ]

  const [reloadKey, triggerReload] = useReloadKey()

  useInterval(triggerReload, 5000)

  useAsyncEffect(async () => {
    if (!skeem.currentSession.id) return
    if (billingStatus.billingData.freeAccount) return
    if (allowedRoutes.some(route => location.pathname.startsWith(route))) return
    if (billingStatus.billingData.hasActiveSubscription) return
    const company = await skeem.fetch2<IRecord<"companies", "isSubCompany"> | null>({
      first: {
        name: "companies",
        matches: { id: globalState.companyId },
        attributes: ["isSubCompany"],
      },
    })
    assert(company, "Company not found")
    if (company.isSubCompany) return

    history.replace(eRoutes["/manage-team/billing"])
    alerts.addAlert({
      content: "Please subscribe to use CleverGoose",
      status: "neutral",
      position: "bottom",
    })
  }, [billingStatus.reloadKey, skeem.currentSession.id, globalState, reloadKey])

  return null
}

function ScrollToTopOnRouteChange() {
  const location = useLocation()

  useEffect(() => {
    if (new URLSearchParams(location.search).has("noScroll")) {
      return
    }
    window.scrollTo({ top: 0 })
  }, [location])

  return null
}

function SwitchBackAfterAdminLoginAsUserButton() {
  const show = useMemo(() => localStorage.getItem("OLD__X-SKEEM_AUTH_TOKEN") !== null, [])

  const handleClick = () => {
    const oldToken = localStorage.getItem("OLD__X-SKEEM_AUTH_TOKEN")
    localStorage.removeItem("OLD__X-SKEEM_AUTH_TOKEN")
    localStorage.setItem("X-SKEEM_AUTH_TOKEN", oldToken!)
    window.location.href = "/"
  }

  if (!show) {
    return null
  }

  return (
    <div
      css={css`
        position: fixed;
        bottom: 20px;
        left: 20px;
        z-index: 999999999999999999999;
      `}
    >
      <Button onClick={handleClick}>Switch Back To Admin</Button>
    </div>
  )
}

function SmartlookInstaller() {
  const [reloadKey, triggerReload] = useReloadKey()

  useInterval(triggerReload, 5000)

  useAsyncEffect(async () => {
    if (process.env.NODE_ENV === "development") {
      return
    }
    Smartlook.init("259373881643fd7957eb4571dbe428240d6a0edf")

    const currentUser = await window.skeem.fetchMe<
      IRecord<"skeem_users", "firstName" | "lastName" | "skeem_email"> & { companyName: string }
    >([
      "firstName",
      "lastName",
      "skeem_email",
      { as: "companyName", value: { path: ["company", "name"] } },
    ])

    if (
      !currentUser ||
      currentUser.skeem_email.toLowerCase().includes("cd2") ||
      currentUser.skeem_email.toLowerCase().includes("clevergoose") ||
      currentUser.skeem_email.toLowerCase().includes("bravegoose")
    ) {
      return
    }

    Smartlook.identify(currentUser.id, {
      name: currentUser.firstName + " " + currentUser.lastName,
      company: currentUser.companyName,
    })
    Smartlook.record({ forms: true, ips: true, emails: true, numbers: true })
  }, [reloadKey])

  return null
}
